import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../core/auth/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { AuthService } from '../../../core/services/application/AuthService';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import AuthLayout from '../../../components/auth/AuthLayout';
import PasswordInput from '../../../components/auth/PasswordInput';
import { sanitizeEmail, sanitizeText } from '../../../core/utils/sanitization';
import { ToastService } from '../../../core/services/ToastService';
import { Logger } from '../../../core/utils/logger';
import { toError } from '../../../core/utils/errorMessage';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [_timeoutOccurred, setTimeoutOccurred] = useState(false);
  const location = useLocation();

  // Check if redirected from successful signup
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    if (success === 'signup') {
      ToastService.success('Account created successfully! Please log in with your credentials.');
    }
  }, [location]);

  // Always call useAuth hook at the top level
  const { login, error: authError } = useAuth();

  // Add timer to show status messages during long operations
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let warningTimeoutId: NodeJS.Timeout;

    if (isLoading) {
      // Set messages based on elapsed time
      timeoutId = setTimeout(() => {
        setProcessingStatus('Still verifying...');

        // Set another timeout for warning
        warningTimeoutId = setTimeout(() => {
          setTimeoutWarning(true);
          setProcessingStatus('This is taking longer than expected. The server might be busy.');
        }, 8000); // Show warning after 8 seconds
      }, 3000); // Show first message after 3 seconds
    } else {
      setProcessingStatus('');
      setTimeoutWarning(false);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(warningTimeoutId);
    };
  }, [isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!login) {
      ToastService.error('Authentication system is not available. Please try again later.');
      return;
    }

    // Clear any previous status
    setProcessingStatus('');
    setTimeoutWarning(false);
    setTimeoutOccurred(false);
    setIsLoading(true);

    try {
      // Sanitize inputs before processing
      const sanitizedEmail = sanitizeEmail(email);
      const sanitizedPassword = sanitizeText(password);

      // Additional validation
      if (!sanitizedEmail || !sanitizedPassword) {
        ToastService.error('Please provide valid email and password');
        setIsLoading(false);
        return;
      }

      await login(sanitizedEmail, sanitizedPassword);

      // Show success message and keep loading state
      ToastService.success('Login successful! Redirecting to your dashboard...');

      // Don't reset loading state - let the cleanup in AuthContext handle it
      // The AuthContext will handle redirection and cleanup
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Logger.error('Login error:', { error: toError(errorMessage) });

      // Check if this was a timeout error
      if (errorMessage.includes('timed out')) {
        setTimeoutOccurred(true);
        ToastService.error(
          'Login is taking longer than expected. Your credentials might be correct, but the server is slow to respond.'
        );
      } else {
        ToastService.error(errorMessage || 'Invalid email or password');
      }

      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      ToastService.error('Please enter your email first');
      return;
    }

    setIsResetLoading(true);

    try {
      // Sanitize email input
      const sanitizedEmail = sanitizeEmail(email);

      if (!sanitizedEmail) {
        ToastService.error('Please enter a valid email address');
        setIsResetLoading(false);
        return;
      }

      const { error } = await AuthService.sendPasswordResetEmail(sanitizedEmail);

      if (error) {
        throw new Error(error);
      }

      ToastService.success('Password reset instructions sent to your email');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Logger.error('Reset password error:', { error: toError(errorMessage) });
      ToastService.error('Failed to send reset instructions. Please try again.');
    } finally {
      setIsResetLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit}>
        {/* Login failures (wrong password, unconfirmed email, timeouts) show
            here and the form stays usable for another attempt */}
        {authError && (
          <div
            role="alert"
            className="mb-5 p-3 bg-red-500/20 text-red-300 dark:text-red-700 rounded-xl text-sm backdrop-blur-sm border border-red-400/30"
          >
            {authError}
          </div>
        )}
        <div className="mb-5">
          <input
            id="name"
            type="email"
            className="w-full px-5 py-4 text-base bg-white/10 border border-white/30 rounded-xl placeholder-white/60 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 backdrop-blur-sm"
            placeholder="Email"
            aria-label="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="mb-6">
          <PasswordInput
            id="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>

        <button
          type="submit"
          className="w-full py-4 px-5 bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90 text-base font-semibold rounded-xl transition duration-200 flex items-center justify-center min-h-[52px]"
          disabled={isLoading}
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </button>

        {/* Show processing status when operation takes a long time */}
        {processingStatus && (
          <div
            className={`mt-4 p-3 ${
              timeoutWarning
                ? 'bg-yellow-500/20 text-yellow-300 dark:text-yellow-700 border-yellow-400/30'
                : 'bg-blue-500/20 text-blue-300 dark:text-blue-700 border-blue-400/30'
            } rounded-xl text-sm backdrop-blur-sm border`}
          >
            {processingStatus}
          </div>
        )}
      </form>

      <div className="mt-5 flex items-center justify-between">
        <Link
          to="/auth/register"
          className="text-base text-white/80 hover:text-white focus:outline-none transition duration-200"
        >
          Create account
        </Link>
        <button
          onClick={handleForgotPassword}
          className="text-base text-white/80 hover:text-white focus:outline-none transition duration-200"
          disabled={isResetLoading}
        >
          {isResetLoading ? (
            <span className="flex items-center">
              <LoadingSpinner />
              <span className="ml-2">Sending Reset Link...</span>
            </span>
          ) : (
            'Forgot password?'
          )}
        </button>
      </div>
    </AuthLayout>
  );
};

export default Login;
